// Workspace switcher (server docs: tenancy.md; chrome docs: widgets.md
// "Workspace switcher"). Company + Space are REAL: the selects hold LIDS
// (opaque handles), the space list follows the selected company
// (g.user.companies), and Apply NAVIGATES to the current page under the
// new tenant prefix — per tab, no server state; the server re-resolves and
// re-gates everything on the way back in.
//
// The ENTITY dimension (widgets.md "The entity dimension" — BSP's ask, Sep
// 2026) is optional and endpoint-fed: g.settings.workspace.entity carries
// the site's declaration ({ param, label, plural, endpoint, landing,
// favorites, current }); when present the modal's search/results panel
// lists the rows the site's endpoint answers for the SELECTED company +
// space (the site's ACL, server-side), picking one sets the entity, and
// Apply carries it in the context (/c/…/s/…/p/…) — staying on the current
// page when its URL already holds the param, landing on `landing`
// otherwise. Favorites are ENTITIES of that dimension (each with its
// company/space, so a favorite is a full destination); the topbar star
// lists them and fills when the current entity is one. Where the list
// LIVES is the site's choice: g.userData 'workspace/favorites' (the
// default) or the site's own storage behind two routes (`favoritesSource`
// — a favorite that is also a server-side filter cannot live in the
// browser's user-data bag; BSP's ask, Sep 2026). Without the declaration
// none of this runs.

const FAVORITES_KEY = 'workspace/favorites';
const FAVORITES_CAP = 50;

const g = () => window.g;
const t = (phrase, vars) => (window.g && window.g.translate ? window.g.translate(phrase, vars) : phrase);
const esc = (v) => (window.g && window.g.esc ? window.g.esc(v == null ? '' : String(v)) : String(v == null ? '' : v));
const entityCfg = () => (window.g && window.g.settings && window.g.settings.workspace && window.g.settings.workspace.entity) || null;

// Repopulate the space select with the chosen company's spaces (default
// space preselected).
function fillSpaces(spaceSel, companyLid) {
	const entry = (g()?.user?.companies || []).find((c) => c.lid === companyLid);
	spaceSel.innerHTML = '';
	(entry?.spaces || []).forEach((s) => {
		const opt = document.createElement('option');
		opt.value = s.lid;
		opt.textContent = s.name;
		opt.selected = Boolean(s.isDefault);
		spaceSel.appendChild(opt);
	});
}

// The SELECTED company's logo next to the Company select (a native select
// cannot render images inside its options): g.user.companies carries the
// display path; without one the box hides.
function fillLogo(companyLid) {
	const box = document.getElementById('wsCompanyLogo');
	if (!box) return;
	const entry = (g()?.user?.companies || []).find((c) => c.lid === companyLid);
	const filesUrl = g()?.settings?.filesUrl || '';
	const img = box.querySelector('img');
	if (entry?.logo && filesUrl && img) {
		img.src = /^https?:\/\//.test(entry.logo) ? entry.logo : `${filesUrl}/${entry.logo}`; // absolute = the identity provider's (oidc.md "Pictures") or a CDN file
		box.hidden = false;
	} else {
		if (img) img.removeAttribute('src');
		box.hidden = true;
	}
}

// ---- favorites (the entity dimension) ---------------------------------------
// One per-user list, entities only (Andrei's ruling): [{ lid, name, meta,
// company, space }]. Two storages, one list for every surface:
//  - the framework's: g.userData 'workspace/favorites', capped, newest first;
//  - the site's (cfg.favoritesSource = { endpoint, toggle }): the list is
//    what `endpoint` answers — held here, re-read at boot, on every modal /
//    star-menu open, after every toggle and on g.workspace.refreshFavorites()
//    (the page that writes the same storage by its own star calls it); the
//    order and the cap are the storage's.
const favoritesSource = () => { const cfg = entityCfg(); return (cfg && cfg.favoritesSource) || null; };
const held = { list: [], seq: 0 };

const cleanFavorites = (list) => (Array.isArray(list) ? list : []).filter((f) => f && f.lid != null && f.lid !== '').map((f) => ({ lid: String(f.lid), name: String(f.name ?? f.lid), meta: f.meta ? String(f.meta) : '', company: f.company ? String(f.company) : '', space: f.space ? String(f.space) : '' }));

function favoritesList() {
	if (favoritesSource()) return held.list;
	const list = g()?.userData ? g().userData.get(FAVORITES_KEY, []) : [];
	return Array.isArray(list) ? list.filter((f) => f && typeof f.lid === 'string') : [];
}

function isFavorite(lid) {
	return favoritesList().some((f) => f.lid === lid);
}

// The site's list: a background read, guarded by the generation counter (a
// toggle or a newer read in flight makes this answer stale — dropped whole).
// A failed read keeps the held list (the chrome never blanks on a hiccup).
async function loadFavorites() {
	const source = favoritesSource();
	if (!source) { renderFavoriteState(); return; }
	if (!g()?.fetch) return;
	if (g().settings?.device?.active === false) return; // an inactive machine asks nothing (api.md "One active machine per user")
	const seq = ++held.seq;
	const res = await g().fetch(source.endpoint, {}, { progress: false, unguarded: true });
	if (seq !== held.seq) return;
	if (!res || !res.success || (res.error && res.error.code)) return;
	held.list = cleanFavorites(res.data);
	renderFavoriteState();
}

// the default (g.userData) storage's writes, serialized (toggleFavorite)
let favoritesWrites = Promise.resolve();

async function toggleFavorite(row) {
	const on = !isFavorite(row.lid);
	const fail = (res) => { if (g().error) g().error((res && res.error && res.error.message) || t('Something went wrong.')); return null; };
	const source = favoritesSource();
	if (source) {
		// the site's storage: it decides (its cap, its ACL — a refusal is a
		// business error whose message shows); the answer's truth is re-read
		const res = await g().fetch(source.toggle, { lid: row.lid, on, company: row.company || null, space: row.space || null }, { progress: false });
		if (!res || !res.success || (res.error && res.error.code)) return fail(res);
		held.seq++; // a read in flight predates the write
		held.list = on ? [...cleanFavorites([row]), ...held.list.filter((f) => f.lid !== row.lid)] : held.list.filter((f) => f.lid !== row.lid);
		renderFavoriteState();
		await loadFavorites();
		return on;
	}
	if (!g()?.userData) return false;
	// the whole list is ONE key: two toggles on different rows (each its
	// own g.action lock) would both read the list before either answered,
	// and the later write would drop the earlier star — so the writes run
	// one after another, each reading the list the previous one stored
	const write = favoritesWrites.then(async () => {
		let list = favoritesList().filter((f) => f.lid !== row.lid);
		if (on) list = [{ lid: row.lid, name: row.name, meta: row.meta || '', company: row.company, space: row.space }, ...list].slice(0, FAVORITES_CAP);
		const res = await g().userData.set(FAVORITES_KEY, list);
		if (res && res.success === false) return fail(res);
		renderFavoriteState();
		return on;
	});
	favoritesWrites = write.catch(() => null);
	return write;
}

// Navigate to a destination under a new tenant — the URL is the state
// (tenancy.md); g.route.url swaps the context prefix. Shared by the
// modal's Apply, the dropup entries and the favorites menu. With an
// entity: `entityLid` (a lid) rides the context param; null drops it;
// undefined keeps whatever the page carries. Where it lands: the CURRENT
// page when its URL already carries the entity param (the page is inside
// the dimension), the declared `landing` pattern otherwise (Andrei's rule
// — a pattern consuming {param} fills it from the context).
function navigateTo(companyLid, spaceLid, entityLid) {
	if (!companyLid || !g()?.route) return;
	const route = g().route;
	const current = route.current();
	const context = { company: companyLid };
	if (spaceLid !== null && spaceLid !== undefined) context.space = spaceLid;
	const cfg = entityCfg();
	let pattern = current.pattern || '/';
	let params = { ...(current.params || {}) };
	if (cfg) {
		if (entityLid === null) context[cfg.param] = false;
		else if (entityLid !== undefined) {
			context[cfg.param] = entityLid;
			const carried = route.context(cfg.param);
			if ((carried === null || carried === undefined) && cfg.landing) { pattern = cfg.landing; params = {}; }
		}
	}
	let target;
	try {
		target = route.url(pattern, params, { context });
	} catch {
		target = route.url('/', {}, { context }); // param-less fallback
	}
	window.location.assign(target);
}

// The dropup presentation (site.workspace.presentation 'dropup' — vux #63):
// each entry carries its destination lids; picking one navigates
// immediately, no Apply. In mode 'company' the entries carry no space —
// the resolver lands on the company's default.
function initDropup() {
	document.querySelectorAll('.workspace-option').forEach((btn) => {
		btn.addEventListener('click', () => {
			navigateTo(btn.getAttribute('data-ws-company'), btn.getAttribute('data-ws-space'), null);
		});
	});
}

// ---- the topbar star: the favorites menu (the star fills when the current
// entity is a favorite; adding one happens where it reads — a switcher row) ----
function renderStarMenu() {
	const cfg = entityCfg();
	const list = document.getElementById('favoritesList');
	if (!cfg || !list) return;
	const favorites = favoritesList();
	list.innerHTML = favorites.length
		? favorites.map((f) => `<button type="button" class="favorite-item${cfg.current && f.lid === cfg.current.lid ? ' active' : ''}" data-fav-lid="${esc(f.lid)}" data-fav-company="${esc(f.company || '')}" data-fav-space="${esc(f.space || '')}"><span class="favorite-icon"><i class="ri-folder-3-line" aria-hidden="true"></i></span><span class="favorite-text"><span class="favorite-name">${esc(f.name)}</span><span class="favorite-meta">${esc(f.meta || '')}</span></span><i class="ri-check-line favorite-check" aria-hidden="true"></i></button>`).join('')
		: `<div class="favorites-empty">${esc(t('No favorites yet.'))}</div>`;
	const star = document.getElementById('favoritesStar');
	if (star && cfg.current) star.className = isFavorite(cfg.current.lid) ? 'ri-star-fill' : 'ri-star-line';
}

function initStar() {
	const cfg = entityCfg();
	const list = document.getElementById('favoritesList');
	if (!cfg || !list) return;
	renderStarMenu();
	list.addEventListener('click', (e) => {
		const item = e.target.closest('.favorite-item');
		if (!item) return;
		const company = item.getAttribute('data-fav-company') || g().route.context('company');
		const space = item.getAttribute('data-fav-space') || g().route.context('space');
		navigateTo(company, space || null, item.getAttribute('data-fav-lid'));
	});
	// the site's list: re-read when the menu opens (another tab, the page's own star)
	document.getElementById('favoritesToggle')?.addEventListener('show.bs.dropdown', () => { if (favoritesSource()) loadFavorites(); });
}

// Every surface that shows a favorite state repaints from the one list; the
// page hears it too (`workspace:favorites` on document — a list page whose
// rows carry their own star follows a toggle made in the chrome).
function renderFavoriteState() {
	renderStarMenu();
	if (modalState) { modalState.renderFavorites(); modalState.paintStars(); }
	document.dispatchEvent(new CustomEvent('workspace:favorites', { detail: { favorites: favoritesList().map((f) => ({ ...f })) } }));
}

let modalState = null;

// Centered workspace modal: real company/space selects + the entity panel.
function initModal() {
	const modalEl = document.getElementById('workspaceModal');
	if (!modalEl || !window.bootstrap) return;

	const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
	const search = document.getElementById('workspaceSearchInput');
	const results = document.getElementById('workspaceResults');
	const company = document.getElementById('wsCompany');
	const space = document.getElementById('wsSpace'); // absent in mode 'company'
	const pickEl = document.getElementById('wsEntityPick'); // absent without the entity dimension
	const favBox = document.getElementById('wsFavorites');
	const cfg = entityCfg();

	// ---- the entity panel state ----
	const state = {
		rows: [],          // the endpoint's rows for the selected tenant
		picked: cfg && cfg.current ? cfg.current.lid : null,
		pickedName: cfg && cfg.current ? cfg.current.name : '',
		seq: 0,
		activeCompany: g()?.route?.context('company') || (company ? company.value : null),
		activeSpace: g()?.route?.context('space') || (space ? space.value : null),
	};

	const rowsHtml = (rows) => rows.map((r) => `<button type="button" class="ws-result${state.picked === r.lid ? ' active' : ''}" data-lid="${esc(r.lid)}" data-name="${esc(r.name)}" data-meta="${esc(r.meta || '')}"><span class="ws-result-icon"><i class="ri-folder-3-line" aria-hidden="true"></i></span><span class="ws-result-text"><span class="ws-result-title">${esc(r.name)}</span>${r.meta ? `<span class="ws-result-meta">${esc(r.meta)}</span>` : ''}</span><span class="ws-result-star${isFavorite(r.lid) ? ' is-on' : ''}" role="button" tabindex="0" aria-label="${esc(t('Toggle favorite'))}"><i class="${isFavorite(r.lid) ? 'ri-star-fill' : 'ri-star-line'}" aria-hidden="true"></i></span></button>`).join('');

	const renderRows = () => {
		if (!results || !cfg) return;
		const label = results.querySelector('.ws-modal-label');
		const empty = results.querySelector('.ws-modal-empty');
		results.querySelectorAll('.ws-result').forEach((el) => el.remove());
		if (empty) empty.insertAdjacentHTML('beforebegin', rowsHtml(state.rows));
		else results.insertAdjacentHTML('beforeend', rowsHtml(state.rows));
		if (label) label.hidden = false;
		filter();
	};

	const paintPick = () => {
		if (!pickEl) return;
		pickEl.textContent = state.pickedName || '—';
		results?.querySelectorAll('.ws-result').forEach((el) => el.classList.toggle('active', el.getAttribute('data-lid') === state.picked));
	};

	// the rows for the SELECTED company + space, from the site's endpoint
	// (its ACL, server-side); a generation counter drops stale answers
	// (a fast tenant change mid-fetch) — the lister's own guard model
	const load = async () => {
		if (!cfg || !results || !g()?.fetch) return;
		const seq = ++state.seq;
		results.classList.add('is-loading');
		const data = { company: company ? company.value : null };
		if (space) data.space = space.value;
		const res = await g().fetch(cfg.endpoint, data, { progress: false, unguarded: true });
		if (seq !== state.seq) return;
		results.classList.remove('is-loading');
		state.rows = res && res.success && !(res.error && res.error.code) && Array.isArray(res.data) ? res.data.filter((r) => r && r.lid != null).map((r) => ({ lid: String(r.lid), name: String(r.name ?? r.lid), meta: r.meta ? String(r.meta) : '' })) : [];
		renderRows();
		paintPick();
	};

	const tenantOf = () => ({ company: company ? company.value : state.activeCompany, space: space ? space.value : state.activeSpace });

	state.renderFavorites = () => {
		if (!favBox || !cfg) return;
		const favorites = favoritesList();
		favBox.innerHTML = favorites.length
			? favorites.map((f) => `<button type="button" class="ws-fav${state.picked === f.lid ? ' active' : ''}" data-lid="${esc(f.lid)}" data-name="${esc(f.name)}" data-meta="${esc(f.meta || '')}" data-company="${esc(f.company || '')}" data-space="${esc(f.space || '')}"><span class="ws-fav-name">${esc(f.name)}</span><span class="ws-fav-meta">${esc(f.meta || '')}</span></button>`).join('')
			: `<div class="ws-modal-empty">${esc(t('No favorites yet.'))}</div>`;
	};
	state.paintStars = () => {
		results?.querySelectorAll('.ws-result').forEach((el) => {
			const on = isFavorite(el.getAttribute('data-lid'));
			const star = el.querySelector('.ws-result-star');
			if (!star) return;
			star.classList.toggle('is-on', on);
			star.querySelector('i').className = on ? 'ri-star-fill' : 'ri-star-line';
		});
	};
	modalState = cfg ? state : null;

	if (company) {
		company.addEventListener('change', () => {
			if (space) fillSpaces(space, company.value);
			fillLogo(company.value);
			state.picked = null;
			state.pickedName = '';
			paintPick();
			load();
		});
		fillLogo(company.value); // initial state (the ACTIVE company)
	}
	if (space) {
		space.addEventListener('change', () => {
			state.picked = null;
			state.pickedName = '';
			paintPick();
			load();
		});
	}

	// Apply: navigate to THIS page (or the landing) under the selected
	// tenant + entity (navigateTo). No pick after a tenant change = the
	// entity param is dropped (the current one belongs to the old space).
	const apply = document.getElementById('wsApply');
	if (apply) {
		apply.addEventListener('click', () => {
			if (!company || !g()?.route) {
				modal.hide();
				return;
			}
			const { company: c, space: s } = tenantOf();
			let entity;
			if (!cfg) entity = undefined;
			else if (state.picked) entity = state.picked;
			else entity = (c === state.activeCompany && s === state.activeSpace) ? undefined : null;
			navigateTo(c, space ? s : null, entity);
		});
	}

	// Picking: a result or a favorite sets the entity; a favorite also
	// moves the tenant selects to its company/space. The star inside a
	// result toggles the favorite without picking.
	const pick = (el) => {
		state.picked = el.getAttribute('data-lid');
		state.pickedName = el.getAttribute('data-name') || '';
		paintPick();
		state.renderFavorites();
	};
	results?.addEventListener('click', (e) => {
		const star = e.target.closest('.ws-result-star');
		const el = e.target.closest('.ws-result');
		if (!el) return;
		if (star) {
			e.stopPropagation();
			const { company: c, space: s } = tenantOf();
			g().action(null, () => toggleFavorite({ lid: el.getAttribute('data-lid'), name: el.getAttribute('data-name'), meta: el.getAttribute('data-meta'), company: c, space: s }), { key: 'workspace:fav:' + el.getAttribute('data-lid') });
			return;
		}
		pick(el);
	});
	results?.addEventListener('keydown', (e) => {
		const star = e.target.closest('.ws-result-star');
		if (star && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); star.click(); }
	});
	favBox?.addEventListener('click', (e) => {
		const el = e.target.closest('.ws-fav');
		if (!el) return;
		const c = el.getAttribute('data-company');
		const s = el.getAttribute('data-space');
		if (company && c && company.value !== c && company.tagName === 'SELECT') {
			company.value = c;
			if (space) fillSpaces(space, c);
			fillLogo(c);
		}
		if (space && s && space.value !== s) space.value = s;
		pick(el);
		load();
	});

	// Live search filter over the rows
	const filter = () => {
		if (!search || !results) return;
		const q = (search.value || '').trim().toLowerCase();
		let any = false;
		results.querySelectorAll('.ws-result').forEach((item) => {
			const hay = `${item.getAttribute('data-name')} ${item.getAttribute('data-meta')}`.toLowerCase();
			const show = hay.includes(q);
			item.hidden = !show;
			if (show) any = true;
		});
		const empty = results.querySelector('.ws-modal-empty');
		if (empty) empty.hidden = any;
	};

	if (search) {
		search.addEventListener('input', filter);
		// Enter picks the first visible result
		search.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') {
				const first = results.querySelector('.ws-result:not([hidden])');
				if (first) {
					e.preventDefault();
					pick(first);
				}
			}
		});
	}

	// Clear the search BEFORE the modal renders (so typing right away isn't
	// wiped by a late handler), then focus once it's visible; the entity
	// rows load on every open (fresh, the endpoint is the truth).
	modalEl.addEventListener('show.bs.modal', () => {
		if (search) {
			search.value = '';
			filter();
		}
		if (cfg) {
			state.renderFavorites();
			paintPick();
			load();
			if (favoritesSource()) loadFavorites();
		}
	});
	modalEl.addEventListener('shown.bs.modal', () => {
		if (search) search.focus();
	});
}

// ---- opening the switcher from the page (BSP's ask, Sep 2026) -------------
// g.workspace.open(focus?) — the modal (or the dropup list in that
// presentation); focus 'entity' lands with the entity search focused
// (the modal does that on its own when the panel exists). Returns false
// when the page renders no switcher at all (nothing to switch, no entity
// dimension). Delegated: any [workspace-open] element, and a link whose
// href is "#workspace" — the menu-row form: a row is DATA ({ label, icon,
// url: '#workspace' }), it cannot carry JS.
function openWorkspace(focus) {
	const modalEl = document.getElementById('workspaceModal');
	if (modalEl && window.bootstrap) {
		const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
		if (focus === 'entity') {
			modalEl.addEventListener('shown.bs.modal', () => { const s = document.getElementById('workspaceSearchInput'); if (s) s.focus(); }, { once: true });
		}
		// Bootstrap drops a show() during the hide fade (display still block,
		// .show already gone): an opener clicked right after a close would do
		// nothing — queue it for the end of the fade instead
		const hiding = !modalEl.classList.contains('show') && modalEl.style.display === 'block';
		if (hiding) modalEl.addEventListener('hidden.bs.modal', () => modal.show(), { once: true });
		else modal.show();
		return true;
	}
	const toggle = document.querySelector('.sidebar-workspace .workspace-toggle');
	if (toggle) { toggle.click(); return true; } // the dropup presentation
	return false;
}

function initOpeners() {
	document.addEventListener('click', (e) => {
		if (!(e.target instanceof Element)) return;
		const el = e.target.closest('[workspace-open], a[href="#workspace"], a[href$="#workspace"]');
		if (!el) return;
		e.preventDefault();
		openWorkspace(el.getAttribute('workspace-open') || undefined);
	});
	if (g()) g().workspace = Object.assign(g().workspace || {}, { open: openWorkspace, refreshFavorites: loadFavorites });
}

export function initWorkspace() {
	initModal();
	initStar();
	// the site's list: the boot read (only where a favorites surface rendered)
	if (favoritesSource() && (document.getElementById('favoritesList') || document.getElementById('wsFavorites'))) loadFavorites();
	initDropup();
	initOpeners();
}
